/**
 * SISP Payment Service
 * Cliente para API de pagamentos SISP
 */

import api from './api';

// ============================================
// Types & Interfaces
// ============================================

export interface SISPPayment {
  id: string;
  merchantRef: string;
  merchantSession: string;
  amount: number;
  status: 'S' | 'N' | 'F' | 'E'; // Submitted, Paid, Failed, Refunded
  paymentMethod: string;
  origin: string;
  createdAt: string;
  invoiceReference?: string;
  customerName?: string;
  entityName?: string;
  transactionId?: string;
  clearingPeriod?: string;
  errorCode?: string;
  errorDescription?: string;
}

export interface SISPPaymentDetail extends SISPPayment {
  facturaEntidadeId?: string;
  invoiceTotal?: number;
  customerEmail?: string;
  customerPhone?: string;
  entityCode?: string;
  responses?: SISPResponse[];
  refunds?: SISPRefund[];
}

export interface SISPResponse {
  id: string;
  merchantRespMerchantRef: string;
  merchantRespErrorCode: string;
  merchantRespErrorDescription: string;
  messageType: string;
  merchantRespCP: string;
  merchantRespTid: string;
  merchantResp: string;
  merchantRespClientReceipt?: string;
  createdAt: string;
}

export interface SISPRefund {
  id: string;
  merchantRef: string;
  amount: number;
  status: string;
  clearingPeriod: string;
  transactionId: string;
  createdAt: string;
}

export interface SISPStats {
  total: number;
  success: number;
  failed: number;
  pending: number;
  refunded: number;
  totalAmount: number;
  successAmount: number;
}

export interface SISPDailyStats {
  date: string;
  total: number;
  success: number;
  failed: number;
  amount: number;
}

export interface SISPMethodStats {
  method: string;
  count: number;
  amount: number;
}

export interface SISPOriginStats {
  origin: string;
  count: number;
  amount: number;
}

export interface SISPPOS {
  id: string;
  designacao: string;
  pos: string;
  url: string;
  ambiente: string;
  activo: string;
  createdAt: string;
}

export interface SISPPaymentEntity {
  id: string;
  descricao: string;
  codigoSisp: string;
  grupo: string;
  activo: string;
  createdAt: string;
}

export interface SISPLog {
  id: string;
  pedidoId: string;
  tipo: string;
  metodo: string;
  url: string;
  responseCode: number;
  duracao: number;
  createdAt: string;
  merchantRef?: string;
}

export interface PaymentFilters {
  page?: number;
  limit?: number;
  status?: string;
  entidadeId?: string;
  startDate?: string;
  endDate?: string;
  origem?: string;
  search?: string;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface StatsResponse {
  success: boolean;
  data: {
    summary: SISPStats;
    dailyPayments: SISPDailyStats[];
    methodStats: SISPMethodStats[];
    originStats: SISPOriginStats[];
  };
}

// ============================================
// SISP Service Class
// ============================================

class SISPServiceClient {

  // ============================================
  // Payment Methods
  // ============================================

  /**
   * Lista pagamentos SISP com filtros e paginacao
   */
  async getPayments(filters: PaymentFilters = {}): Promise<PaginatedResponse<SISPPayment>> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.append(key, String(value));
        }
      });

      const response = await api.get(`/admin/sisp/payments?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching SISP payments:', error);
      throw error;
    }
  }

  /**
   * Busca detalhes de um pagamento especifico
   */
  async getPaymentDetails(id: string): Promise<{ success: boolean; data: SISPPaymentDetail }> {
    try {
      const response = await api.get(`/admin/sisp/payments/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching payment details:', error);
      throw error;
    }
  }

  /**
   * Busca estatisticas de pagamentos
   */
  async getStats(period: number = 30): Promise<StatsResponse> {
    try {
      const response = await api.get(`/admin/sisp/payments/stats?period=${period}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching SISP stats:', error);
      throw error;
    }
  }

  // ============================================
  // Refund Methods
  // ============================================

  /**
   * Solicita estorno de um pagamento
   */
  async requestRefund(pedidoId: string, motivo?: string): Promise<{
    success: boolean;
    data: { refundId: string; merchantRef: string };
    message: string;
  }> {
    try {
      const response = await api.post('/admin/sisp/refund', {
        pedidoId,
        motivo,
      });
      return response.data;
    } catch (error) {
      console.error('Error requesting refund:', error);
      throw error;
    }
  }

  async forceReconcile(merchantRef: string, confirm = false): Promise<{
    success: boolean;
    message: string;
    dryRun?: boolean;
    pedidoId?: number;
    sisp?: { id: number; status: string; tipo: string; valor: string };
    pedido?: { id: number; status: string; pagamento_status: string };
  }> {
    const response = await api.post('/sisp/admin/force-reconcile', { merchantRef, confirm });
    return response.data;
  }

  // ============================================
  // Configuration Methods
  // ============================================

  /**
   * Lista POS SISP configurados
   */
  async getPOSList(activo?: string): Promise<{ success: boolean; data: SISPPOS[] }> {
    try {
      const params = activo ? `?activo=${activo}` : '';
      const response = await api.get(`/admin/sisp/pos${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching POS list:', error);
      throw error;
    }
  }

  /**
   * Lista entidades de pagamento SISP
   */
  async getPaymentEntities(filters?: { activo?: string; grupo?: string }): Promise<{
    success: boolean;
    data: SISPPaymentEntity[];
  }> {
    try {
      const params = new URLSearchParams();
      if (filters?.activo) params.append('activo', filters.activo);
      if (filters?.grupo) params.append('grupo', filters.grupo);

      const response = await api.get(`/admin/sisp/entities?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching payment entities:', error);
      throw error;
    }
  }

  // ============================================
  // Log Methods
  // ============================================

  /**
   * Lista logs de comunicacao SISP
   */
  async getLogs(filters: {
    page?: number;
    limit?: number;
    pedidoId?: string;
    tipo?: string;
    startDate?: string;
    endDate?: string;
  } = {}): Promise<PaginatedResponse<SISPLog>> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.append(key, String(value));
        }
      });

      const response = await api.get(`/admin/sisp/logs?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching SISP logs:', error);
      throw error;
    }
  }

  /**
   * Busca detalhes de um log especifico
   */
  async getLogDetails(id: string): Promise<{ success: boolean; data: any }> {
    try {
      const response = await api.get(`/admin/sisp/logs/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching log details:', error);
      throw error;
    }
  }

  // ============================================
  // Utility Methods
  // ============================================

  /**
   * Mapeia status code para label
   */
  getStatusLabel(status: string): string {
    const statusMap: Record<string, string> = {
      'S': 'Submetido',
      'N': 'Pago',
      'F': 'Falhou',
      'E': 'Estornado',
    };
    return statusMap[status] || status;
  }

  /**
   * Mapeia status code para cor
   */
  getStatusColor(status: string): 'success' | 'error' | 'warning' | 'info' | 'default' {
    const colorMap: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
      'S': 'warning',
      'N': 'success',
      'F': 'error',
      'E': 'info',
    };
    return colorMap[status] || 'default';
  }

  /**
   * Mapeia origem para label
   */
  getOriginLabel(origin: string): string {
    const originMap: Record<string, string> = {
      'WEBS': 'Consulta AEPS',
      'WEBSR': 'Servico Referencia',
      'RCG': 'Recarga',
      'RCP': 'Recarga Pagali',
      'EVT': 'Evento',
      'DNT': 'Donativo',
      'ECM': 'E-commerce',
      'FAT': 'Fatura',
      'ESTB': 'Balcao Virtual',
    };
    return originMap[origin] || origin;
  }

  /**
   * Formata montante em CVE
   */
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-CV', {
      style: 'currency',
      currency: 'CVE',
    }).format(value);
  }

  // ============================================
  // Token Methods
  // ============================================

  /**
   * Lista tokens de clientes
   */
  async getTokens(filters: {
    page?: number;
    limit?: number;
    clientId?: string;
    activo?: string;
    search?: string;
  } = {}): Promise<PaginatedResponse<SISPToken>> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.append(key, String(value));
        }
      });

      const response = await api.get(`/admin/sisp/tokens?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching SISP tokens:', error);
      throw error;
    }
  }

  /**
   * Marca/desmarca token como token do sistema
   */
  async setSystemToken(tokenId: string, isSystemToken: boolean): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.patch(`/admin/sisp/tokens/${tokenId}/set-system`, { isSystemToken });
      return response.data;
    } catch (error) {
      console.error('Error setting system token:', error);
      throw error;
    }
  }

  /**
   * Desativa um token
   */
  async deleteToken(tokenId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`/admin/sisp/tokens/${tokenId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting token:', error);
      throw error;
    }
  }
  /**
   * Consultar estado de transação no SISP
   */
  async updateTransactionEntityName(id: string, entidadeNome: string): Promise<{ success: boolean; message: string }> {
    const response = await api.put(`/admin/sisp/transactions/${id}/entity-name`, { entidadeNome });
    return response.data;
  }

  async queryTransactionStatus(params: {
    merchantRef: string;
    posId?: string;
    posAuthCode?: string;
    environment?: 'sandbox' | 'production';
  }): Promise<{
    success: boolean;
    data?: {
      result: boolean;
      msg: string;
      transactionSuccess: boolean;
      transactionStatusDescription: string;
    };
    error?: string;
  }> {
    try {
      const response = await api.post('/admin/sisp/transaction-status', params);
      return response.data;
    } catch (error) {
      console.error('Error querying transaction status:', error);
      throw error;
    }
  }

  /** Extrato de vendas por POS ID (para vigiar os limites diários da SISP). */
  async getPosStats(date?: string): Promise<PosStatsResponse> {
    const qs = date ? `?date=${encodeURIComponent(date)}` : '';
    const response = await api.get(`/admin/sisp/pos-stats${qs}`);
    return response.data?.data as PosStatsResponse;
  }

  /** Define o limite diário da SISP de um POS (null limpa). */
  async setPosLimite(posId: string, limite: number | null): Promise<void> {
    await api.put(`/admin/sisp/pos/${encodeURIComponent(posId)}/limite`, { limite_diario_cve: limite });
  }
}

export interface PosStatsRow {
  posId: string;
  servico: string | null;
  ambiente: string | null;
  banco: string | null;
  total: number;
  nTransacoes: number;
  limite: number | null;
  pct: number | null;
}
export interface PosStatsResponse {
  date: string;
  pos: PosStatsRow[];
  semPos: { total: number; nTransacoes: number };
  totalDia: { total: number; nTransacoes: number };
}

// Token interface
export interface SISPToken {
  id: string;
  token: string;
  lastFourDigits: string;
  cardType: string;
  cardHolder: string;
  expireMonth: string;
  expireYear: string;
  activo: string;
  createdAt: string;
  lastUsedAt: string | null;
  clienteId: string;
  clienteNome: string;
  clienteEmail: string;
  clienteTelefone: string;
}

export const sispService = new SISPServiceClient();
export default sispService;
